import { useMemo, useState } from 'react';
import type {
  Assinatura,
  AssinaturaCiclo,
  GrantSubscriptionInput,
  Plano,
} from '@/types/subscription.types';
import {
  GRANT_TIPO_PADRAO,
  computeVigenciaEnd,
  computePeriodEndByCiclo,
  precoMensalReais,
  emptyLimites,
} from './constants';
import type { LimitesForm, ModalTema, PlanoCard, VigenciaAnos } from './types';

// Pré-preenche o card/ciclo selecionados a partir da assinatura atual do produtor
// (ex: quem já é 'profissional'/'anual' abre o drawer com esse card ativo).
function inferPlanoCardFromAssinatura(
  assinatura: Assinatura | null,
  plans: Plano[] | undefined
): { planoCard: PlanoCard; ciclo: AssinaturaCiclo } {
  if (!assinatura) return { planoCard: 'trial', ciclo: 'mensal' };
  if (assinatura.status === 'trial') return { planoCard: 'trial', ciclo: 'mensal' };

  const hasOverrideLimites =
    assinatura.override_limite_animais != null ||
    assinatura.override_limite_funcionarios != null ||
    assinatura.override_limite_propriedades != null;

  // Básico/Profissional (inclusive pago pelo painel ou pela AbacatePay, que
  // gravam valor) continuam no card do plano; Enterprise é o contrato sem plano
  // do catálogo ou com limites próprios.
  const plano = plans?.find((p) => p.id === assinatura.plano_id);
  if ((plano?.codigo === 'basico' || plano?.codigo === 'profissional') && !hasOverrideLimites)
    return { planoCard: plano.codigo, ciclo: assinatura.ciclo ?? 'mensal' };
  if (hasOverrideLimites || assinatura.valor_total != null || assinatura.valor != null)
    return { planoCard: 'enterprise', ciclo: assinatura.ciclo ?? 'mensal' };

  return { planoCard: 'trial', ciclo: 'mensal' };
}

// "1.234,56" ou "1234.56" → número; vazio/inválido → null.
function parseValor(texto: string): number | null {
  const limpo = texto.trim().replace(/\./g, '').replace(',', '.');
  const n = texto.includes(',') ? Number(limpo) : Number(texto.trim());
  return texto.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null;
}

export function useSubscriptionModalForm(
  assinatura: Assinatura | null,
  plans: Plano[] | undefined
) {
  const initial = useMemo(
    () => inferPlanoCardFromAssinatura(assinatura, plans),
    [assinatura, plans]
  );

  const [planoCard, setPlanoCard] = useState<PlanoCard>(initial.planoCard);
  const [ciclo, setCicloState] = useState<AssinaturaCiclo>(initial.ciclo);
  const [vigenciaAnos, setVigenciaAnos] = useState<VigenciaAnos>(1);
  const [limites, setLimites] = useState<LimitesForm>(
    assinatura?.override_limite_animais != null ||
      assinatura?.override_limite_funcionarios != null ||
      assinatura?.override_limite_propriedades != null
      ? {
          animais:
            assinatura.override_limite_animais != null
              ? String(assinatura.override_limite_animais)
              : '',
          funcionarios:
            assinatura.override_limite_funcionarios != null
              ? String(assinatura.override_limite_funcionarios)
              : '',
          propriedades:
            assinatura.override_limite_propriedades != null
              ? String(assinatura.override_limite_propriedades)
              : '',
        }
      : emptyLimites
  );
  const [valorTotal, setValorTotal] = useState<string>(
    assinatura?.valor_total != null ? String(assinatura.valor_total) : ''
  );
  const [formaPagamento, setFormaPagamento] = useState<'mensal' | 'integral'>(
    assinatura?.forma_pagamento ?? 'mensal'
  );
  const [notasAdmin, setNotasAdmin] = useState<string>(assinatura?.notas_admin ?? '');
  // Cortesia (planos padrão): ON = concede grátis (status comp); OFF = assinatura
  // paga (status active, com valor/período). Só uma assinatura já 'active' abre OFF.
  const [cortesia, setCortesia] = useState<boolean>(assinatura?.status !== 'active');
  // Enterprise é contrato pago por padrão; cortesia só quando o admin liga a chave
  // (contratos antigos gravados como cortesia por engano abrem como pagos).
  const [enterpriseCortesia, setEnterpriseCortesia] = useState<boolean>(false);
  // Valor pago (R$) do Básico/Profissional: null = o preço da tabela do ciclo.
  // Anual é o total do ano, pago de uma vez.
  const [valorPagoEditado, setValorPagoEditado] = useState<string | null>(
    assinatura?.status === 'active' && assinatura.plano_id != null && assinatura.valor != null
      ? String(
          assinatura.forma_pagamento === 'integral' && assinatura.valor_total != null
            ? assinatura.valor_total
            : assinatura.valor
        )
      : null
  );
  // Renovação do plano pago: automática por link (fatura 5 dias antes, Pix ou
  // boleto, avisos no WhatsApp) ou manual pelo painel.
  const [cobrancaAutomatica, setCobrancaAutomatica] = useState<boolean>(
    assinatura?.modo_cobranca ? assinatura.modo_cobranca !== 'nenhum' : true
  );
  // Trocar o ciclo volta o valor para o preço da tabela do novo ciclo.
  const setCiclo = (novo: AssinaturaCiclo) => {
    setCicloState(novo);
    setValorPagoEditado(null);
  };

  const tema: ModalTema = planoCard === 'enterprise' ? 'verde' : 'azul';

  const planoSelecionado: Plano | null = useMemo(() => {
    if (planoCard !== 'basico' && planoCard !== 'profissional') return null;
    return plans?.find((p) => p.codigo === planoCard) ?? null;
  }, [planoCard, plans]);

  // Limites efetivos do modo Padrão (read-only, vêm do plano/trial).
  const limitesPadrao = useMemo(() => {
    if (planoCard === 'trial') {
      // Trial usa os limites do plano básico como piso mínimo de avaliação
      // (mesma regra do backend: trial não tem plano_id, mas o entitlement
      // resolve um limite default — aqui mostramos o do básico como referência).
      const basico = plans?.find((p) => p.codigo === 'basico');
      return {
        animais: basico?.limite_animais ?? null,
        funcionarios: basico?.limite_funcionarios ?? null,
        propriedades: basico?.limite_propriedades ?? null,
      };
    }
    if (!planoSelecionado) return { animais: null, funcionarios: null, propriedades: null };
    return {
      animais: planoSelecionado.limite_animais,
      funcionarios: planoSelecionado.limite_funcionarios,
      propriedades: planoSelecionado.limite_propriedades,
    };
  }, [planoCard, planoSelecionado, plans]);

  // Preço da tabela para o ciclo escolhido (R$): mensal = a mensalidade, anual =
  // o total do ano.
  const valorTabela = useMemo(() => {
    if (!planoSelecionado) return null;
    const centavos =
      ciclo === 'anual'
        ? planoSelecionado.preco_anual_centavos
        : planoSelecionado.preco_mensal_centavos;
    return centavos != null ? centavos / 100 : null;
  }, [planoSelecionado, ciclo]);
  const valorPago = valorPagoEditado ?? (valorTabela != null ? valorTabela.toFixed(2) : '');
  const valorPagoNumero = parseValor(valorPago);

  // Valor mensal efetivo do modo Padrão (R$/mês), null se ainda não tem plano/preço.
  const valorMensalPadrao = useMemo(() => {
    if (planoCard === 'trial') return 0;
    if (!planoSelecionado) return null;
    return precoMensalReais(
      planoSelecionado.preco_mensal_centavos,
      planoSelecionado.preco_anual_centavos,
      ciclo
    );
  }, [planoCard, planoSelecionado, ciclo]);

  // Vigência do contrato Enterprise ("até DD/MM/AAAA").
  const vigenciaEnd = useMemo(() => computeVigenciaEnd(vigenciaAnos), [vigenciaAnos]);

  // Parcela calculada do Enterprise: total / nMeses (só quando forma = mensal).
  const nMeses = vigenciaAnos * 12;
  const parcela = useMemo(() => {
    const total = Number(valorTotal.replace(',', '.'));
    if (!valorTotal.trim() || !Number.isFinite(total) || total <= 0) return null;
    if (formaPagamento === 'integral') return total;
    return total / nMeses;
  }, [valorTotal, formaPagamento, nMeses]);

  // Monta o payload final (GrantSubscriptionInput) pro POST /subscription/grant.
  const buildPayload = (): GrantSubscriptionInput => {
    if (planoCard === 'trial') {
      return {
        tipo: 'trial_15d',
        ...(notasAdmin.trim() ? { notas_admin: notasAdmin.trim() } : {}),
      };
    }
    if (planoCard === 'basico' || planoCard === 'profissional') {
      // Cortesia: preset padrão (status comp / grátis no backend).
      if (cortesia) {
        const tipo = GRANT_TIPO_PADRAO[planoCard][ciclo === 'anual' ? 'anual' : 'mensal'];
        return { tipo, ...(notasAdmin.trim() ? { notas_admin: notasAdmin.trim() } : {}) };
      }
      // Assinatura paga: custom herdando os limites do plano, status active + o
      // valor pago. Anual é pago de uma vez (integral, com o total); mensal é a parcela.
      const anual = ciclo === 'anual';
      return {
        tipo: 'custom',
        status: 'active',
        plano_id: planoSelecionado?.id ?? null,
        ciclo,
        current_period_end: computePeriodEndByCiclo(anual ? 'anual' : 'mensal').toISOString(),
        origem: 'admin_custom',
        override_limite_animais: null,
        override_limite_funcionarios: null,
        override_limite_propriedades: null,
        valor: valorPagoNumero,
        forma_pagamento: anual ? 'integral' : 'mensal',
        valor_total: anual ? valorPagoNumero : null,
        notas_admin: notasAdmin.trim() || null,
        modo_cobranca: cobrancaAutomatica ? 'manual_link' : 'nenhum',
      };
    }

    // Enterprise -> custom. Contrato pago (active); cortesia só com a chave ligada.
    const total = valorTotal.trim() === '' ? null : Number(valorTotal.replace(',', '.'));
    if (enterpriseCortesia) {
      return {
        tipo: 'custom',
        status: 'comp',
        plano_id: null,
        ciclo: null,
        current_period_end: vigenciaEnd.toISOString(),
        origem: 'admin_comp',
        override_limite_animais: limites.animais ? parseInt(limites.animais, 10) : null,
        override_limite_funcionarios: limites.funcionarios
          ? parseInt(limites.funcionarios, 10)
          : null,
        override_limite_propriedades: limites.propriedades
          ? parseInt(limites.propriedades, 10)
          : null,
        valor: null,
        forma_pagamento: null,
        valor_total: null,
        notas_admin: notasAdmin.trim() || null,
      };
    }
    return {
      tipo: 'custom',
      status: 'active',
      plano_id: null,
      ciclo: null,
      current_period_end: vigenciaEnd.toISOString(),
      origem: 'admin_custom',
      override_limite_animais: limites.animais ? parseInt(limites.animais, 10) : null,
      override_limite_funcionarios: limites.funcionarios
        ? parseInt(limites.funcionarios, 10)
        : null,
      override_limite_propriedades: limites.propriedades
        ? parseInt(limites.propriedades, 10)
        : null,
      valor: parcela,
      forma_pagamento: formaPagamento,
      valor_total: total != null && Number.isFinite(total) ? total : null,
      notas_admin: notasAdmin.trim() || null,
    };
  };

  return {
    tema,
    planoCard,
    setPlanoCard,
    ciclo,
    setCiclo,
    vigenciaAnos,
    setVigenciaAnos,
    limites,
    setLimites,
    valorTotal,
    setValorTotal,
    formaPagamento,
    setFormaPagamento,
    notasAdmin,
    setNotasAdmin,
    cortesia,
    setCortesia,
    enterpriseCortesia,
    setEnterpriseCortesia,
    cobrancaAutomatica,
    setCobrancaAutomatica,
    valorPago,
    setValorPago: setValorPagoEditado,
    valorPagoNumero,
    valorTabela,
    planoSelecionado,
    limitesPadrao,
    valorMensalPadrao,
    vigenciaEnd,
    nMeses,
    parcela,
    buildPayload,
  };
}

export type SubscriptionModalFormState = ReturnType<typeof useSubscriptionModalForm>;
